import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AuthService } from 'src/app/auth.service';
import { ToastService } from 'src/app/utils/toast.service';
import { handleFormErrors } from 'src/app/utils/handleFormErrors';

function checkPasswordsMatch(group: FormGroup) {
  const newPassword = group.get('newPassword')?.value;
  const newPassword2 = group.get('newPassword2')?.value;

  return newPassword === newPassword2 ? null : { notSame: true };
}

@Component({
  selector: 'app-force-change-password-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './force-change-password-modal.component.html',
  styleUrls: ['./force-change-password-modal.component.scss'],
})
export class ForceChangePasswordModalComponent implements OnInit {
  @Input() userId!: number;

  form!: FormGroup;
  formErrors: any = null;
  backendErrors: Record<string, string> = {};
  isLoading = false;

  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  newPasswordWatched = '';
  confirmPasswordWatched = '';

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private authService: AuthService,
    private toastService: ToastService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.form = this.initForm();

    this.form.get('newPassword')?.valueChanges.subscribe((value) => {
      this.newPasswordWatched = value;
    });
    this.form.get('newPassword2')?.valueChanges.subscribe((value) => {
      this.confirmPasswordWatched = value;
    });

    this.form.valueChanges.subscribe(() => this.refreshErrors());
    this.form.statusChanges.subscribe(() => this.refreshErrors());
  }

  onSubmit(): void {
    if (this.isLoading) return;

    Object.values(this.form.controls).forEach((control) => control.markAsTouched());
    this.backendErrors = {};

    const { errors, totalErrors } = handleFormErrors(this.form);
    this.formErrors = errors;

    if (totalErrors || this.form.hasError('notSame')) {
      return;
    }

    const { currentPassword, newPassword, newPassword2 } = this.form.value;
    this.isLoading = true;

    this.authService
      .changePassword(this.userId, {
        password: currentPassword,
        new_password: newPassword,
        new_password2: newPassword2,
      })
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.toastService.showToastSuccess(
            this.translate.instant('auth.force-password-change.success'),
            ''
          );
          this.activeModal.close('changed');
        },
        error: (error) => {
          this.isLoading = false;
          this.applyBackendErrors(error);
        },
      });
  }

  private initForm(): FormGroup {
    return this.fb.group(
      {
        currentPassword: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(20)]],
        newPassword: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(20)]],
        newPassword2: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(20)]],
      },
      { validators: checkPasswordsMatch }
    );
  }

  private refreshErrors(): void {
    if (!this.formErrors) return;

    const { errors } = handleFormErrors(this.form);
    const next: any = {};
    Object.keys(errors).forEach((name) => {
      if (this.form.get(name)?.touched) {
        next[name] = errors[name];
      }
    });
    this.formErrors = next;
  }

  private applyBackendErrors(error: any): void {
    const body = error?.error ?? {};
    this.backendErrors = {
      currentPassword: this.extractMessage(body.password),
      newPassword: this.extractMessage(body.new_password),
      newPassword2: this.extractMessage(body.new_password2),
    };

    const generic = this.extractMessage(body.non_field_errors) || this.extractMessage(body.detail);
    const hasFieldError = this.backendErrors['currentPassword'] || this.backendErrors['newPassword'] || this.backendErrors['newPassword2'];

    if (generic && !hasFieldError) {
      this.toastService.showToastError(generic, '');
    }
  }

  /**
   * Los errores de DRF suelen venir como string u ["string"], pero algunos
   * endpoints los envuelven en objetos ({ message, code, ... }) o arrays de
   * esos objetos — bajamos recursivamente hasta encontrar el texto real.
   */
  private extractMessage(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);

    if (Array.isArray(value)) {
      return value.length ? this.extractMessage(value[0]) : '';
    }

    if (typeof value === 'object') {
      if (typeof value.message === 'string') return value.message;
      if (typeof value.detail === 'string') return value.detail;
      if (typeof value.error === 'string') return value.error;

      const nested = Object.values(value);
      return nested.length ? this.extractMessage(nested[0]) : '';
    }

    return '';
  }
}
